/**
 * FloatingSignupCta — desktop-only floating conversion card.
 *
 * The problem: users scroll around the landing page reading content and
 * leave without signing up. The hero CTAs are above-the-fold-only; the
 * navbar "Student login" is not a signup entry point. This card sits
 * bottom-right as a persistent, non-modal signup nudge.
 *
 * Complementary to ``StickyMobileCta`` (which handles mobile) — this one
 * is desktop-only (``hidden md:block``) so we never double-stack.
 *
 * UX:
 *   * Appears after user has scrolled 400px OR spent 8s on the page
 *     (whichever comes first). That way even users who don't scroll get
 *     the nudge, but we don't slam it in their face immediately.
 *   * Dismissible with 24h memory (localStorage). We WANT to re-nudge
 *     the day after — first-visit dismissal shouldn't mean forever.
 *   * Bounce-in animation via inline transition; no framer dependency.
 *   * Doesn't render on `/dashboard`, `/portal/*` (already auth'd) or
 *     when a modal is open.
 */
import { useEffect, useState } from "react";
import { X, ArrowRight, Sparkles } from "lucide-react";

const DISMISS_KEY = "ac_floater_dismissed_at_v1";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const SHOW_AFTER_PX = 400;
const SHOW_AFTER_MS = 8_000;

export default function FloatingSignupCta({ onSignup, hidden = false }) {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (hidden) return;
    // Respect a recent dismissal (24h cooldown).
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const at = parseInt(raw, 10);
        if (!Number.isNaN(at) && Date.now() - at < DISMISS_TTL_MS) return;
      }
    } catch (_) { /* privacy mode — proceed */ }

    let scrollSeen = false;
    const onScroll = () => {
      if (!scrollSeen && (window.scrollY || 0) > SHOW_AFTER_PX) {
        scrollSeen = true;
        show();
      }
    };
    const timer = setTimeout(show, SHOW_AFTER_MS);

    function show() {
      setVisible(true);
      // Kick the CSS transition one frame later so it animates in.
      requestAnimationFrame(() => setEntered(true));
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, [hidden]);

  if (hidden || !visible) return null;

  const dismiss = () => {
    setEntered(false);
    // Delay unmount so the exit transition can play out.
    setTimeout(() => setVisible(false), 200);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
  };

  return (
    <div
      className={`hidden md:block fixed bottom-6 right-6 z-40 w-[340px] transition-all duration-300 ${
        entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      data-testid="floating-signup-cta"
      role="complementary"
      aria-label="Sign up prompt"
    >
      <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.25)]">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss signup prompt"
          className="absolute top-2.5 right-2.5 z-20 w-7 h-7 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex items-center justify-center transition-colors"
          data-testid="floating-signup-cta-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {/* Coloured left accent strip for visual grounding. */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500" />

        <div className="pl-5 pr-4 py-4">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase mb-2">
            <Sparkles className="w-3 h-3" /> Free to try
          </div>
          <h3 className="font-display text-[15px] font-bold text-slate-900 leading-snug">
            See your CET shortlist in 2 minutes.
          </h3>
          <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
            Personalised College picks based on your marks. No card, no spam — just a peek.
          </p>
          <button
            type="button"
            onClick={onSignup}
            className="mt-3 w-full h-10 rounded-lg text-[13px] font-bold text-slate-900 bg-amber-500 hover:bg-amber-400 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
            data-testid="floating-signup-cta-btn"
          >
            Register free <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <p className="text-[10.5px] text-slate-400 text-center mt-1.5">
            Takes ~30 sec · One-time ₹99 unlocks full shortlist
          </p>
        </div>
      </div>
    </div>
  );
}
